import { NavLink } from 'react-router-dom'
import type { NavLeaf } from './navigation'
import { useCanAccess, useCanPlatform } from '@/features/auth/access'
import { useTerm } from '@/features/terminology/use-term'
import { cn } from '@/lib/utils'

/**
 * Renders a single nav leaf, but only if access checks pass.
 * Returns null (renders nothing) when the user can't access the route.
 */
export function SidebarNavItem({ leaf, collapsed }: { leaf: NavLeaf; collapsed: boolean }) {
  const term = useTerm()
  const canAccess = useCanAccess({ permission: leaf.permission, module: leaf.module })
  const canPlatform = useCanPlatform(leaf.permission)

  // DistroOps internal admin items — gated by platform.* permission.
  if (leaf.platform) {
    if (!canPlatform) return null
  } else if (!canAccess) {
    return null
  }

  const Icon = leaf.icon
  const label = leaf.termKey ? term(leaf.termKey) : leaf.label

  return (
    <NavLink
      to={leaf.to}
      end={leaf.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent/10 hover:text-accent',
          isActive
            ? 'bg-accent/15 text-accent'
            : 'text-muted-foreground',
          collapsed && 'justify-center px-2',
        )
      }
      title={collapsed ? label : undefined}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  )
}
